export type NormalizedAuthResponse =
  | {
      token: string | null;
      status: number | null;
      permission: number | null;
      userId: string | null;
    }
  | null;

const TOKEN_KEYS = ["jwt", "jwttoken", "jwt_token", "token", "accesstoken", "access_token"];
const PERMISSION_KEYS = ["permission", "permissions", "perm", "role", "admin", "is_admin", "isadmin"];
const USER_ID_KEYS = ["user_id", "userid", "id_user", "customer_id", "client_id", "account_id"];

const normalizeObjectEntries = (source: Record<string, unknown>): Record<string, unknown> =>
  Object.entries(source).reduce<Record<string, unknown>>((acc, [key, value]) => {
    const normalizedKey = key.toLowerCase();
    if (!(normalizedKey in acc)) {
      acc[normalizedKey] = value;
    }
    return acc;
  }, {});

const getTokenValue = (source: Record<string, unknown>): string | null => {
  const normalizedEntries = normalizeObjectEntries(source);

  for (const key of TOKEN_KEYS) {
    const value = normalizedEntries[key];
    if (typeof value === "string" && value.length > 0) {
      return value;
    }
  }

  return null;
};

const extractJwtToken = (payload: unknown): string | null => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const record = payload as Record<string, unknown>;

  const directToken = getTokenValue(record);
  if (directToken) {
    return directToken;
  }

  const data = record.data;
  if (data && typeof data === "object") {
    const dataToken = getTokenValue(data as Record<string, unknown>);
    if (dataToken) {
      return dataToken;
    }
  }

  return null;
};

const parsePermissionValue = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(0, Math.floor(value));
  }
  if (typeof value === "boolean") {
    return value ? 1 : 0;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed.length) {
      return null;
    }
    const numeric = Number(trimmed);
    if (Number.isFinite(numeric)) {
      return Math.max(0, Math.floor(numeric));
    }
  }
  return null;
};

const parseUserIdValue = (value: unknown): string | null => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return null;
};

const extractPermissionValue = (source: Record<string, unknown>): number | null => {
  const normalizedEntries = normalizeObjectEntries(source);

  for (const key of PERMISSION_KEYS) {
    if (!(key in normalizedEntries)) {
      continue;
    }
    const parsed = parsePermissionValue(normalizedEntries[key]);
    if (parsed !== null) {
      return parsed;
    }
  }

  return null;
};

const extractUserIdValue = (source: Record<string, unknown>): string | null => {
  const normalizedEntries = normalizeObjectEntries(source);

  for (const key of USER_ID_KEYS) {
    if (!(key in normalizedEntries)) {
      continue;
    }
    const parsed = parseUserIdValue(normalizedEntries[key]);
    if (parsed !== null) {
      return parsed;
    }
  }

  return null;
};

export function normalizeAuthApiResponse(payload: unknown | null): NormalizedAuthResponse {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const record = payload as Record<string, unknown>;

  const directToken = extractJwtToken(record);
  const directStatus = typeof record.status === "number" ? record.status : null;
  const directPermission = extractPermissionValue(record);
  const directUserId = extractUserIdValue(record);

  let nestedToken: string | null = null;
  let nestedStatus: number | null = null;
  let nestedPermission: number | null = null;
  let nestedUserId: string | null = null;

  const nestedSources = [record.data, record["_data_"]];
  for (const nested of nestedSources) {
    if (!nested || typeof nested !== "object") {
      continue;
    }

    const dataRecord = nested as Record<string, unknown>;
    const candidateToken = extractJwtToken(dataRecord);
    const candidateStatus = typeof dataRecord.status === "number" ? dataRecord.status : null;
    const candidatePermission = extractPermissionValue(dataRecord);
    const candidateUserId = extractUserIdValue(dataRecord);

    if (candidateToken !== null && nestedToken === null) {
      nestedToken = candidateToken;
    }

    if (candidateStatus !== null && nestedStatus === null) {
      nestedStatus = candidateStatus;
    }

    if (candidatePermission !== null && nestedPermission === null) {
      nestedPermission = candidatePermission;
    }

    if (candidateUserId !== null && nestedUserId === null) {
      nestedUserId = candidateUserId;
    }

    if (nestedToken !== null && nestedStatus !== null && nestedPermission !== null && nestedUserId !== null) {
      break;
    }
  }

  return {
    token: directToken ?? nestedToken,
    status: directStatus ?? nestedStatus,
    permission: directPermission ?? nestedPermission,
    userId: directUserId ?? nestedUserId,
  };
}
